import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  ListChecks,
  Settings,
  LogOut,
  ChevronsUpDown,
  ShieldAlert,
  Shield,
  Database,
  FileText,
  History,
  Server,
  Cookie,
  Inbox,
  Users,
  Package,
  Lock,
  Building2,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { LogoFull } from './Logo';
import { useAuth } from '../lib/auth';
import { hasFeature, isRouteLockedForTrial, planStatusEffective } from '../lib/plans';
import { useRouteProgress } from '../lib/useRouteProgress';
import PlanBadge from './PlanBadge';
import OrgSwitcher from './OrgSwitcher';

const SECTIONS = [
  {
    title: 'Panel general',
    items: [
      { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/app/diagnostico', label: 'Diagnóstico', icon: ClipboardList, testid: 'nav-diagnostic' },
      { to: '/app/plan', label: 'Plan de acción', icon: ListChecks, testid: 'nav-plan' },
    ],
  },
  {
    title: 'Tu ruta · Ley 21.719',
    items: [
      { to: '/app/sistemas', label: 'Sistemas y proveedores', icon: Server, testid: 'nav-sistemas', step: 1 },
      { to: '/app/rat', label: 'Inventario de datos', icon: Database, testid: 'nav-rat', step: 2 },
      { to: '/app/dpia', label: 'Evaluación de riesgos', icon: Shield, testid: 'nav-dpia', step: 3 },
      { to: '/app/documentos', label: 'Documentos legales', icon: FileText, testid: 'nav-docs', step: 4 },
      { to: '/app/cookies', label: 'Banner de cookies', icon: Cookie, testid: 'nav-cmp', step: 5 },
      { to: '/app/arco', label: 'Solicitudes de titulares', icon: Inbox, testid: 'nav-arco', step: 6 },
      { to: '/app/brechas', label: 'Emergencias (72h)', icon: ShieldAlert, testid: 'nav-brechas', step: 7 },
    ],
  },
  {
    title: 'Evidencia y auditoría',
    items: [
      { to: '/app/trazabilidad', label: 'Bitácora de acciones', icon: History, testid: 'nav-trazabilidad' },
      { to: '/app/fiscalizacion', label: 'Carpeta de evidencias', icon: Package, testid: 'nav-fiscalizacion' },
    ],
  },
  {
    title: 'Administración',
    items: [
      { to: '/app/equipo', label: 'Equipo', icon: Users, testid: 'nav-team' },
      { to: '/app/configuracion', label: 'Configuración', icon: Settings, testid: 'nav-settings' },
    ],
  },
];

const FUTURE = [];

export default function Sidebar() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const { progress, done: routeDone } = useRouteProgress();

  const initials = user ? `${user.nombre?.[0] || ''}${user.apellido?.[0] || ''}`.toUpperCase() : 'P';

  // Aviso de vencimiento próximo (< 7 días) o vencido
  const expiryBanner = (() => {
    if (!organization?.plan_expires_at) return null;
    const status = planStatusEffective(organization);
    const daysLeft = Math.ceil(
      (new Date(organization.plan_expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
    );
    if (status === 'expired' || daysLeft < 0) {
      return { tone: 'red', title: 'Plan vencido', message: 'Contacte a Priviax para renovar.' };
    }
    if (daysLeft <= 7) {
      return {
        tone: 'orange',
        title: daysLeft === 0 ? 'Vence hoy' : `Vence en ${daysLeft} día${daysLeft === 1 ? '' : 's'}`,
        message: 'Renueve su plan para evitar la suspensión.',
      };
    }
    return null;
  })();

  return (
    <aside
      className="hidden lg:flex flex-col w-[260px] shrink-0 h-screen sticky top-0 bg-[#0B1220] border-r border-[#0F172A]"
      data-testid="app-sidebar"
    >
      {/* Header org / switcher */}
      <div className="px-3 pt-4 pb-3 border-b border-white/5">
        <OrgSwitcher />
      </div>

      {/* Nav */}
      <nav className="px-3 py-4 space-y-4 flex-1 overflow-y-auto">
        {SECTIONS.map((section) => (
          <div key={section.title}>
            <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-white/85 flex items-center justify-between gap-2">
              <span>{section.title}</span>
              {section.title === 'Tu ruta · Ley 21.719' && progress && (
                <span className="text-[10px] font-semibold text-[#7EE0AC] tabular-nums normal-case tracking-normal" data-testid="nav-route-progress">
                  {routeDone}/7
                </span>
              )}
            </div>
            {section.title === 'Tu ruta · Ley 21.719' && progress && (
              <div className="mx-3 mb-2 h-1 rounded-full bg-white/10 overflow-hidden" data-testid="nav-route-bar">
                <div
                  className="h-full bg-[#2A9B68] transition-[width] duration-300"
                  style={{ width: `${(routeDone / 7) * 100}%` }}
                />
              </div>
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                const featureLocked = item.feature && !hasFeature(organization, item.feature);
                const trialLocked = isRouteLockedForTrial(item.to, organization);
                const locked = featureLocked || trialLocked;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    className={({ isActive }) => `pv-nav-link ${isActive ? 'active' : ''} ${locked ? 'opacity-70' : ''}`}
                    title={trialLocked ? 'Requiere plan activo. Contrate un plan para desbloquear.' : undefined}
                  >
                    {(() => {
                      if (!item.step) return <item.icon size={16} strokeWidth={1.5} />;
                      const st = progress?.[item.to];
                      if (st === 'done') {
                        return (
                          <span className="w-4 h-4 shrink-0 rounded-full bg-[#2A9B68] flex items-center justify-center text-white" data-testid={`${item.testid}-status`} title="Paso completado">
                            <Check size={10} strokeWidth={3} />
                          </span>
                        );
                      }
                      if (st === 'attention') {
                        return (
                          <span className="w-4 h-4 shrink-0 rounded-full border border-[#F59E0B] flex items-center justify-center" data-testid={`${item.testid}-status`} title="Requiere atención">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
                          </span>
                        );
                      }
                      return (
                        <span className="w-4 h-4 shrink-0 rounded-full border border-white/30 text-[9.5px] font-semibold flex items-center justify-center text-white/80 tabular-nums" data-testid={`${item.testid}-step`} title="Paso pendiente">
                          {item.step}
                        </span>
                      );
                    })()}
                    <span className="flex-1">{item.label}</span>
                    {locked && <Lock size={11} className="text-white/60" strokeWidth={2} />}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}

        {FUTURE.length > 0 && (
        <div>
          <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-white/85">Próximamente</div>
          <div className="space-y-1">
            {FUTURE.map((f) => (
              <div
                key={f.label}
                className="flex items-center justify-between px-3 py-2 rounded-md text-[13px] text-white/70 cursor-not-allowed select-none"
              >
                <span>{f.label}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 border border-white/15 uppercase tracking-wider text-white/80">
                  {f.hint}
                </span>
              </div>
            ))}
          </div>
        </div>
        )}
      </nav>

      {/* Footer usuario */}
      <div className="border-t border-white/5 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-[#2A9B68]/20 border border-[#2A9B68]/40 flex items-center justify-center text-[#7EE0AC] text-[12px] font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-white truncate">{user?.nombre} {user?.apellido}</div>
            <div className="text-[11px] text-white/75 truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            className="text-white/75 hover:text-white p-1.5 rounded-md hover:bg-white/10 transition-colors"
            data-testid="sidebar-logout"
            title="Cerrar sesión"
          >
            <LogOut size={15} strokeWidth={1.5} />
          </button>
        </div>
        <div className="mt-3 px-3 py-2.5 rounded-md bg-[#132038] border border-white/10 flex items-start gap-2">
          <ShieldAlert size={14} className="text-[#FBBF24] mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-[11px] text-white/90 leading-snug">
            Ley 21.719 entra en vigencia el <span className="text-white font-medium">1 dic 2026</span>.
          </div>
        </div>
        {expiryBanner && (
          <div
            className={`mt-2 px-3 py-2.5 rounded-md flex items-start gap-2 ${
              expiryBanner.tone === 'red'
                ? 'bg-[#3A0F0F] border border-[#B91C1C]/60'
                : 'bg-[#3A2A0F] border border-[#F59E0B]/60'
            }`}
            data-testid="plan-expiry-banner"
          >
            <AlertTriangle
              size={14}
              className={expiryBanner.tone === 'red' ? 'text-[#F87171] mt-0.5 shrink-0' : 'text-[#FBBF24] mt-0.5 shrink-0'}
              strokeWidth={1.75}
            />
            <div className="text-[11px] leading-snug">
              <div className={`font-semibold ${expiryBanner.tone === 'red' ? 'text-[#FCA5A5]' : 'text-[#FDE68A]'}`}>
                {expiryBanner.title}
              </div>
              <div className="text-white/80 mt-0.5">{expiryBanner.message}</div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
